import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest, HttpParams } from '@angular/common/http';
import { Observable, Subject, timer } from 'rxjs';
import { takeUntil, retryWhen, scan, delayWhen } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class BlobService {
  constructor(private http: HttpClient) {}

  /**
   * Generic upload that posts a FormData to any URL and returns the HttpEvent stream.
   */
  upload(url: string, formData: FormData, params?: Record<string, string | number>): Observable<HttpEvent<any>> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(k => (httpParams = httpParams.set(k, String(params[k]))));
    }

    const req = new HttpRequest('POST', url, formData, {
      reportProgress: true,
      responseType: 'json',
      params: httpParams,
    });

    return this.http.request(req);
  }

  /**
   * Convenience wrapper for the backend media upload endpoint used across the app.
   */
  uploadMedia(formData: FormData): Observable<HttpEvent<any>> {
    // Usa o mesmo base do ApiService
    // @ts-ignore
    const apiBase = require('../../environments/environment').environment?.API_BASE_URL?.replace(/\/$/, '') ?? '/api/v1';
    return this.upload(`${apiBase}/media/upload`, formData);
  }

  /**
   * Upload with explicit cancel and retry control.
   * Returns an object with the event stream and a cancel() function.
   */
  uploadWithCancel(url: string, formData: FormData, opts?: { retries?: number; backoffMs?: number }) {
    const cancel$ = new Subject<void>();
    const retries = opts?.retries ?? 2;
    const backoffMs = opts?.backoffMs ?? 1000;

    const httpParams = undefined; // kept for future params support
    const req = new HttpRequest('POST', url, formData, {
      reportProgress: true,
      responseType: 'json',
    });

    const events$ = this.http.request(req).pipe(
      takeUntil(cancel$),
      retryWhen(errors =>
        errors.pipe(
          scan((retryCount, err) => {
            if (retryCount >= retries) {
              throw err;
            }
            return retryCount + 1;
          }, 0),
          delayWhen(retryCount => timer(retryCount * backoffMs))
        )
      )
    );

    return {
      events$,
      cancel: () => {
        cancel$.next();
        cancel$.complete();
      },
    };
  }
}
